import { Badge, Body1, Caption1, DrawerBody, DrawerHeader, Input, OverlayDrawer, Tab, TabList, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ArrowRotateClockwise16Regular, Checkmark12Filled, ChevronDown12Regular, Copy16Regular, Dismiss24Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppDialog, ConfirmDialog, DialogActionsBar, DialogCheckbox, DialogChoiceGroup, DialogField } from "@/components/app-dialog";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton, FuiFilterSelect } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { ListToolbar } from "@/components/list-toolbar";
import { useDialogLayer } from "@/components/ui/dialog-layer";
import { todayISO } from "@/engine/date";
import { formatDate } from "@/lib/date-format";
import { inspectPlan, planCover, type PlanSnapshot } from "@/features/plans/plan-gallery";
import { useSettingsHub } from "@/shell/settings-hub";
import { useProjectStore } from "@/store/project-store";
import { useCatalogStore } from "@/store/catalog-store";
import { PlanMemberAddDialog, PlanTeamAddDialog } from "@/features/directory/DirectoryDialogs";
import { findPersonByEmail, grantOf, invitedGrant, planAddCandidates, teamGrantRows } from "@/features/directory/directory";
import { putDirectoryGrant, useDirectoryGrants } from "@/features/directory/directory-store";
import { useDirectoryPeople } from "@/features/directory/directory-view";
import { MemberRoster, type MemberListTab } from "./MemberTable";
import { NewShareButton, ShareLinkList } from "./ShareLinkPanel";
import { applyMockAccessScene, archiveShareSentence, auditMatchesAction, auditMatchesPeriod, cancelMockAccessRequest, cancelMockInvite, leaveImpactSentence, leaveMockPlan, pauseSharesOnArchive, MOCK_AUDIT_ROWS, PUBLIC_INVITE_NOTE, PUBLIC_INVITE_OFF, publicInviteCopyText, reauthBlockSentence, reauthPromptSentence, removeMockMember, resendMockInvite, rotatePublicInvite, setMockMemberRole, setMockShareEnabled, setPublicInviteEnabled, sharePolicySentence, suspendMockMember, reactivateMockMember, transferMockOwnership, transferOwnershipImpact, addMockPlanMembers, useMockPlanAccess, type MockAuditRow } from "./mock-plan-access";
import { MockCaseMenu, type MockCase } from "./MockCaseMenu";
import { UI_TERMS } from "./terms";
import { setPlanVisibility, usePlanVisibility, type PlanVisibility } from "./plan-visibility";
import { setPlanViewerComment, usePlanViewerComment } from "./plan-viewer-comment";
import { planRoleLabel, useCan, usePlanActor } from "./use-plan-access";

const AUDIT_PERIODS = [
  { value: "전체", label: "전체" },
  { value: "7일", label: "7일" },
  { value: "30일", label: "30일" },
  { value: "90일", label: "90일" },
];

const DEFAULT_PLAN_AUDIT_MOCKS: MockAuditRow[] = [
  ...MOCK_AUDIT_ROWS,
  { id: "a4", when: "2026-10-26", who: "나", what: "공유 링크 생성", detail: "이해관계자용 실시간 현황 공유 링크를 새로 생성했습니다." },
  { id: "a5", when: "2026-10-25", who: "김PM", what: "기준선 승인", detail: "초기 마일스톤 기준선(Baseline #1)을 승인했습니다." },
  { id: "a6", when: "2026-10-24", who: "이아키", what: "멤버 역할 변경", detail: "박기여 님의 역할을 「기여자」에서 「편집자」로 변경했습니다." },
  { id: "a7", when: "2026-10-20", who: "나", what: "공용 초대 링크 회전", detail: "보안 정책에 따라 기존 공용 초대 링크를 만료하고 새 링크로 회전했습니다." },
  { id: "a8", when: "2026-10-18", who: "김PM", what: "뷰어 댓글 권한 변경", detail: "뷰어의 댓글 작성 권한을 활성화했습니다." },
  { id: "a9", when: "2026-10-15", who: "나", what: "플랜 공개 범위 변경", detail: "플랜 공개 범위를 「비공개」에서 「워크스페이스 공개」로 변경했습니다." },
  { id: "a10", when: "2026-10-10", who: "최보안", what: "보안 정책 점검", detail: "외부 공유 링크 유효 기간 점검을 완료했습니다." },
];

const useStyles = makeStyles({
  shell: {
    display: "flex",
    flex: 1,
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    gap: "12px",
  },
  pane: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflow: "auto",
    paddingTop: 0,
    paddingLeft: 0,
  },
  general: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minWidth: 0,
    width: "100%",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minWidth: 0,
    width: "100%",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "20px",
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  cardHead: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  cardTitle: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  cardSubtitle: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
  },
  divider: {
    height: "1px",
    backgroundColor: tokens.colorNeutralStroke2,
  },
  segmentedControl: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    gap: "2px",
    width: "fit-content",
  },
  segmentedButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "5px 14px",
    borderRadius: tokens.borderRadiusSmall,
    border: "none",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    cursor: "pointer",
    transitionProperty: "background-color, color, box-shadow",
    transitionDuration: tokens.durationFaster,
    ":hover": {
      color: tokens.colorNeutralForeground1,
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  segmentedButtonActive: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    boxShadow: tokens.shadow2,
  },
  fieldHint: {
    display: "block",
    marginTop: "5px",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },
  dangerZone: {
    display: "flex",
    flexDirection: "column",
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: "hidden",
  },
  dangerFoldToggle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "12px 16px",
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    textAlign: "left",
    transitionProperty: "background-color",
    transitionDuration: tokens.durationFaster,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  dangerFoldTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorPaletteRedForeground1,
  },
  dangerContent: {
    display: "flex",
    flexDirection: "column",
    padding: "0 16px 8px",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: tokens.colorNeutralStroke2,
  },
  dangerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    padding: "10px 0",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colorNeutralStroke2,
    ":last-child": { borderBottom: "none" },
  },
  dangerRowText: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
    flex: 1,
  },
  dangerRowTitle: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  actionsBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    paddingTop: "16px",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: tokens.colorNeutralStroke2,
  },
  actionStatus: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  actionSavedCheck: {
    color: tokens.colorPaletteGreenForeground1,
  },
  actionButtonGroup: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginLeft: "auto",
  },
  linkCardBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "12px 16px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalS,
    padding: `4px ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    borderTop: "none",
  },
  nameInput: {
    width: "100%",
    maxWidth: "320px",
  },
  formActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
    paddingTop: "12px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  formError: {
    color: tokens.colorPaletteRedForeground1,
  },
  fill: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    flex: 1,
    minWidth: 0,
    minHeight: 0,
  },
  frame: { width: "100%", flex: 1, minHeight: 0, overflow: "auto" },
  fixed: { tableLayout: "fixed" },
  clip: { overflow: "hidden", maxWidth: 0 },
  name: {
    display: "block",
    maxWidth: "100%",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  quiet: { color: tokens.colorNeutralForeground3 },
  auditRow: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
  drawer: {
    position: "fixed",
    top: "0",
    right: "0",
    height: "100vh",
    width: "360px",
    maxWidth: "100vw",
    pointerEvents: "auto",
    /* 같은 레이어의 대화상자 표면(z-50) 위에 둔다. */
    zIndex: 60,
  },
  drawerHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "8px",
  },
  drawerBody: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  fold: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    width: "100%",
    marginTop: "16px",
    padding: "8px 2px",
    border: "none",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase300,
    textAlign: "left",
    cursor: "pointer",
    ":hover": { color: tokens.colorNeutralForeground1 },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  foldChevron: {
    flexShrink: 0,
    transitionProperty: "transform",
    transitionDuration: tokens.durationFaster,
  },
  foldChevronClosed: {
    transform: "rotate(-90deg)",
  },
  /** 아카이브·이전·나가기. 일반 탭에서 접어 둔다. */
  acts: { flexShrink: 0 },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
  },
  linkActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
  },
  path: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  tabs: {
    flex: "0 0 140px",
    height: "100%",
    gap: "4px",
    paddingTop: "4px",
  },
  tab: {
    justifyContent: "flex-start",
    width: "100%",
    minHeight: "32px",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    borderRadius: tokens.borderRadiusMedium,
    fontWeight: tokens.fontWeightRegular,
    "& .fui-Tab__content": {
      fontWeight: tokens.fontWeightRegular,
      fontSize: tokens.fontSizeBase300,
      lineHeight: tokens.lineHeightBase300,
      whiteSpace: "nowrap",
    },
    "&::before, &::after": {
      content: "none",
    },
    "&[aria-selected='true']": {
      backgroundColor: tokens.colorNeutralBackground3,
    },
    "&[aria-selected='true']:hover": {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
});

const SETTINGS_CASES: MockCase[] = [
  { id: "general", label: "일반" },
  { id: "members", label: "멤버" },
  { id: "no-members", label: "멤버 없음" },
  { id: "statuses", label: "초대 상태 8개" },
  { id: "expired-invite", label: "만료된 초대" },
  { id: "no-requests", label: "접근 요청 없음" },
  { id: "security", label: "보안" },
  { id: "share", label: "공유 켜짐" },
  { id: "share-off", label: "공유 꺼짐" },
  { id: "audit-empty", label: "감사 기록 없음" },
  { id: "audit", label: "감사 기록" },
];

export type PlanSettingsTab = "general" | "members" | "security" | "share" | "audit";

type PlanStatusChoice = "active" | "done" | "";

/** 목록과 같은 상태. 고친 값이 있으면 그것이고, 없으면 일정이다. 지연·일정 없음은 고를 수 없다. */
function statusChoice(project: { lifecycle?: string; statusOverride?: "active" | "done" }, snap: Pick<PlanSnapshot, "tone">): PlanStatusChoice {
  if (project.lifecycle === "archived") return "";
  if (project.statusOverride === "active" || project.statusOverride === "done") return project.statusOverride;
  if (snap.tone === "done") return "done";
  if (snap.tone === "active") return "active";
  return "";
}

/** 라디오가 이미 말하는 진행 중·완료는 빼고, 일정에서만 나오는 사실을 한 줄로 둔다. */
function statusFact(cover: { status: string; detail: string }): string {
  if (cover.status === "진행 중" || cover.status === "완료") return cover.detail;
  return [cover.status, cover.detail].filter(Boolean).join(" · ");
}

const TABS: Array<{ value: PlanSettingsTab; label: string }> = [
  { value: "general", label: "일반" },
  { value: "members", label: "멤버" },
  { value: "security", label: "보안" },
  { value: "share", label: "공유" },
  { value: "audit", label: "감사 로그" },
];

export function PlanSettingsDialog({
  open,
  onClose,
  planName,
  initialTab,
  embedded = false,
  section,
  onSection,
}: {
  open: boolean;
  onClose: () => void;
  planName: string;
  /** 열릴 때 한 번만 이 탭으로 옮긴다. 없으면 마지막으로 보던 탭을 유지한다. */
  initialTab?: PlanSettingsTab;
  /** 사이드 설정. 대화상자 껍질과 왼쪽 탭 없이 현재 항목만 그린다. */
  embedded?: boolean;
  section?: PlanSettingsTab;
  onSection?: (tab: PlanSettingsTab) => void;
}) {
  const styles = useStyles();
  const table = useDataTableStyles();
  const layer = useDialogLayer();
  const access = useMockPlanAccess();
  const actor = usePlanActor();
  const canReview = useCan("plan.member.manage");
  const transferTarget = access.members.find((member) => member.role === "관리자" && member.status === "활성") ?? null;
  const [tab, setTab] = useState<PlanSettingsTab>("general");
  const [memberTab, setMemberTab] = useState<MemberListTab>("members");
  const [name, setName] = useState(planName);
  const project = useProjectStore((s) => s.project);
  const today = todayISO();
  const snap = useMemo(() => inspectPlan(project, today), [project, today]);
  const archived = project.lifecycle === "archived";
  const savedStatus = statusChoice(project, snap);
  const cover = planCover({ ...snap, lifecycle: project.lifecycle ?? "active" }, today);
  const [status, setStatus] = useState<PlanStatusChoice>(savedStatus);
  const planId = useProjectStore((s) => s.activeId);
  const savedVisibility = usePlanVisibility(planId);
  const savedComment = usePlanViewerComment(planId);
  const [visibility, setVisibility] = useState<PlanVisibility>(savedVisibility);
  const [viewerComment, setViewerComment] = useState(savedComment);
  const [savedNote, setSavedNote] = useState(false);
  const [leave, setLeave] = useState<"close" | PlanSettingsTab | null>(null);
  const opened = useRef(false);
  const nameInvalid = name.trim() === "";
  const statusDirty = !archived && status !== savedStatus;
  const dirty = name.trim() !== planName || visibility !== savedVisibility || viewerComment !== savedComment || statusDirty;
  const [confirmName, setConfirmName] = useState("");
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [reauth, setReauth] = useState<"ask" | "blocked" | null>(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [auditEmpty, setAuditEmpty] = useState(false);
  const [auditPeriod, setAuditPeriod] = useState("전체");
  const [auditAction, setAuditAction] = useState("전체");
  const [auditId, setAuditId] = useState<string | null>(null);
  const [shareDetailId, setShareDetailId] = useState<string | null>(null);
  const [grant, setGrant] = useState<"member" | "team" | null>(null);
  const [dangerFold, setDangerFold] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const directory = useDirectoryPeople();
  const grants = useDirectoryGrants();
  const resources = useCatalogStore((s) => s.resources);
  const teams = useCatalogStore((s) => s.teams);
  const addResource = useCatalogStore((s) => s.addResource);
  const rosterEmails = access.members.map((member) => member.email);
  const grantPeople = directory.map((person) => ({
    id: person.id,
    name: person.name,
    email: person.email,
    teamId: person.teamId,
    teamName: person.teamName,
    status: person.grant.status,
    kind: person.grant.kind,
  }));
  const addCandidates = planAddCandidates(grantPeople, rosterEmails);
  const teamChoices = Object.values(teams).map((team) => ({
    id: team.id,
    name: team.name,
    count: teamGrantRows(grantPeople, team.id, rosterEmails).length,
  }));

  const addSelected = (ids: string[]) => {
    const picked = new Set(ids);
    addMockPlanMembers(
      addCandidates.filter((person) => picked.has(person.id)).map((person) => ({ name: person.name, email: person.email, role: "편집자" })),
    );
  };

  const inviteGuest = (email: string): string | null => {
    const address = email.trim();
    if (!address.includes("@") || !address.includes(".")) return "이메일 주소를 입력하세요.";
    const known = Object.values(resources).map((resource) => ({ id: resource.id, email: resource.email }));
    const existingId = findPersonByEmail(known, address);
    if (existingId && grantOf(grants, existingId).status !== "removed") {
      const person = resources[existingId];
      const added = person ? addMockPlanMembers([{ name: person.displayName, email: person.email, role: "편집자" }]) : 0;
      return added ? null : "이미 이 플랜에 있습니다.";
    }
    const resourceId = existingId ?? addResource({
      displayName: address,
      email: address,
      roleTitle: "게스트",
      employmentType: "partner",
      grade: "mid",
      skills: [],
      isActive: true,
    });
    putDirectoryGrant(resourceId, invitedGrant("viewer", "guest"));
    addMockPlanMembers([{ name: address, email: address, role: "뷰어", guest: true }]);
    return null;
  };

  const allAudits = access.audits.length > 3 ? access.audits : DEFAULT_PLAN_AUDIT_MOCKS;
  const auditSource = auditEmpty ? [] : allAudits;
  const auditActions = [
    { value: "전체", label: "전체" },
    ...Array.from(new Set(auditSource.map((row) => row.what)), (what) => ({ value: what, label: what })),
  ];
  const auditShown = auditSource.filter(
    (row) => auditMatchesPeriod(row, auditPeriod) && auditMatchesAction(row, auditAction),
  );
  const auditOpen = auditSource.find((row) => row.id === auditId) ?? null;

  useEffect(() => {
    if (open && !opened.current) {
      setName(planName);
      setVisibility(savedVisibility);
      setViewerComment(savedComment);
      setStatus(savedStatus);
      setSavedNote(false);
      setLeave(null);
      setLinkCopied(false);
      if (initialTab) setTab(initialTab);
    }
    opened.current = open;
  }, [open, planName, savedVisibility, savedComment, savedStatus, initialTab]);

  const resetGeneral = () => {
    setName(planName);
    setVisibility(savedVisibility);
    setViewerComment(savedComment);
    setStatus(savedStatus);
    setSavedNote(false);
  };

  const saveGeneral = () => {
    const nextName = name.trim();
    if (!planId || !nextName || !dirty) return;
    if (nextName !== planName) useProjectStore.getState().setProject({ name: nextName });
    if (statusDirty && (status === "active" || status === "done")) {
      const natural = statusChoice({ ...project, statusOverride: undefined }, inspectPlan({ ...project, statusOverride: undefined }, today));
      useProjectStore.getState().setProject({ statusOverride: status === natural ? undefined : status });
    }
    if (visibility !== savedVisibility) setPlanVisibility(planId, visibility);
    if (viewerComment !== savedComment) setPlanViewerComment(planId, viewerComment);
    setName(nextName);
    setSavedNote(true);
  };

  const applyTab = (next: PlanSettingsTab) => {
    setTab(next);
    setAuditId(null);
    setShareDetailId(null);
    setGrant(null);
  };

  const selectTab = (next: PlanSettingsTab) => {
    if (next === tab) return;
    if (tab === "general" && dirty) {
      setLeave(next);
      return;
    }
    applyTab(next);
  };

  const requestClose = () => {
    if (tab === "general" && dirty) {
      setLeave("close");
      return;
    }
    onClose();
  };

  const pendingSection = useRef<string | null>(null);

  useEffect(() => {
    if (!embedded || !section || section === tab) return;
    selectTab(section);
  }, [embedded, section, tab, dirty]);

  useEffect(() => {
    if (!embedded) return;
    useSettingsHub.getState().setCloseGuard(() => {
      if (tab === "general" && dirty) {
        pendingSection.current = null;
        setLeave("close");
        return false;
      }
      return true;
    });
    useSettingsHub.getState().setSectionGuard((next) => {
      if (tab === "general" && dirty && next !== "general") {
        pendingSection.current = next;
        setLeave("close");
        return false;
      }
      return true;
    });
    return () => {
      useSettingsHub.getState().setCloseGuard(null);
      useSettingsHub.getState().setSectionGuard(null);
    };
  }, [embedded, tab, dirty]);

  const confirmLeave = () => {
    const next = leave;
    const dest = pendingSection.current;
    pendingSection.current = null;
    resetGeneral();
    setLeave(null);
    if (dest) {
      useSettingsHub.getState().commitSection(dest);
      return;
    }
    if (next === "close") onClose();
    else if (next) applyTab(next);
  };

  const pickCase = (id: string) => {
    setAuditId(null);
    setAuditEmpty(id === "audit-empty");
    if (id === "audit" || id === "audit-empty") {
      setAuditPeriod("전체");
      setAuditAction("전체");
    }
    if (id === "general") {
      applyMockAccessScene("default");
      setTab("general");
    } else if (id === "members") {
      applyMockAccessScene("default");
      setTab("members");
      setMemberTab("members");
    } else if (id === "no-members") {
      applyMockAccessScene("no-members");
      setTab("members");
      setMemberTab("members");
    } else if (id === "no-requests") {
      applyMockAccessScene("no-requests");
      setTab("members");
      setMemberTab("requests");
    } else if (id === "statuses") {
      applyMockAccessScene("statuses");
      setTab("members");
      setMemberTab("members");
    } else if (id === "expired-invite") {
      applyMockAccessScene("expired-invite");
      setTab("members");
      setMemberTab("invites");
    } else if (id === "security") {
      applyMockAccessScene("default");
      setTab("security");
    } else if (id === "share") {
      applyMockAccessScene("default");
      setTab("share");
    } else if (id === "share-off") {
      applyMockAccessScene("share-off");
      setTab("share");
    } else if (id === "audit" || id === "audit-empty") {
      setTab("audit");
    }
  };

  const settingsPane = (
          <div className={styles.pane}>
          {tab === "general" && (
            <div className={styles.general}>
              {/* 플랜 설정 카드 */}
              <div className={styles.card}>
                <div className={styles.cardHead}>
                  <div className={styles.cardTitle}>플랜 설정</div>
                  <Caption1 className={styles.cardSubtitle}>
                    플랜 이름, 진행 상태 및 공개 권한을 설정합니다.
                  </Caption1>
                </div>

                <DialogField label="플랜 이름" required>
                  <Input
                    className={styles.nameInput}
                    value={name}
                    aria-invalid={nameInvalid}
                    aria-label="플랜 이름"
                    placeholder="예: 2027 모빌리티 인프라 구축"
                    onChange={(_, data) => {
                      setName(data.value);
                      setSavedNote(false);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && dirty && !nameInvalid) {
                        event.preventDefault();
                        saveGeneral();
                      }
                    }}
                  />
                  {nameInvalid ? <Caption1 className={styles.formError}>플랜 이름을 입력하세요</Caption1> : null}
                </DialogField>

                <div>
                  <Caption1 className={styles.quiet} style={{ marginBottom: "6px", display: "block" }}>
                    진행 상태
                  </Caption1>
                  {archived ? (
                    <Badge size="medium" appearance="tint" color="warning">
                      아카이브됨 (읽기 전용)
                    </Badge>
                  ) : (
                    <>
                      <div className={styles.segmentedControl} role="radiogroup" aria-label="진행 상태">
                        <button
                          type="button"
                          role="radio"
                          aria-checked={status === "active"}
                          className={mergeClasses(styles.segmentedButton, status === "active" && styles.segmentedButtonActive)}
                          onClick={() => {
                            setStatus("active");
                            setSavedNote(false);
                          }}
                        >
                          진행 중
                        </button>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={status === "done"}
                          className={mergeClasses(styles.segmentedButton, status === "done" && styles.segmentedButtonActive)}
                          onClick={() => {
                            setStatus("done");
                            setSavedNote(false);
                          }}
                        >
                          완료
                        </button>
                      </div>
                      <Caption1 className={styles.fieldHint}>
                        {status === "active"
                          ? "마감이 지나도 계속 일정을 관리합니다."
                          : "진척도와 무관하게 프로젝트를 완료 상태로 마감합니다."}
                      </Caption1>
                    </>
                  )}
                  {statusFact(cover) ? (
                    <Caption1 className={styles.quiet} style={{ marginTop: "4px", display: "block" }}>
                      {statusFact(cover)}
                    </Caption1>
                  ) : null}
                </div>

                <div className={styles.divider} />

                <div>
                  <Caption1 className={styles.quiet} style={{ marginBottom: "6px", display: "block" }}>
                    공개 범위
                  </Caption1>
                  <div className={styles.segmentedControl} role="radiogroup" aria-label="공개 범위">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={visibility === "private"}
                      className={mergeClasses(styles.segmentedButton, visibility === "private" && styles.segmentedButtonActive)}
                      onClick={() => {
                        setVisibility("private");
                        setSavedNote(false);
                      }}
                    >
                      비공개
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={visibility === "workspace"}
                      className={mergeClasses(styles.segmentedButton, visibility === "workspace" && styles.segmentedButtonActive)}
                      onClick={() => {
                        setVisibility("workspace");
                        setSavedNote(false);
                      }}
                    >
                      워크스페이스 공개
                    </button>
                  </div>
                  <Caption1 className={styles.fieldHint}>
                    {visibility === "private"
                      ? "소유자 및 초대받은 멤버만 플랜을 볼 수 있습니다."
                      : "워크스페이스 모든 참여자가 읽기 권한으로 열람할 수 있습니다."}
                  </Caption1>
                </div>

                <div>
                  <DialogCheckbox
                    label="뷰어(읽기 권한자)의 댓글 작성을 허용합니다"
                    checked={viewerComment}
                    onChange={(next) => {
                      setViewerComment(next);
                      setSavedNote(false);
                    }}
                  />
                </div>

                {/* 하단 액션 바 */}
                <div className={styles.actionsBar}>
                  <div className={styles.actionStatus}>
                    {savedNote && !dirty ? (
                      <>
                        <Checkmark12Filled className={styles.actionSavedCheck} />
                        <span>변경사항이 저장되었습니다.</span>
                      </>
                    ) : dirty ? (
                      <span>수정된 내용이 있습니다.</span>
                    ) : null}
                  </div>
                  <div className={styles.actionButtonGroup}>
                    <FuiButton
                      appearance="secondary"
                      density="dialog"
                      disabled={!dirty}
                      onClick={() => {
                        setName(planName);
                        setVisibility(savedVisibility);
                        setViewerComment(savedComment);
                        setStatus(savedStatus);
                        setSavedNote(false);
                      }}
                    >
                      취소
                    </FuiButton>
                    <FuiButton
                      appearance="primary"
                      density="dialog"
                      disabled={!dirty || nameInvalid}
                      onClick={saveGeneral}
                    >
                      변경사항 저장
                    </FuiButton>
                  </div>
                </div>
              </div>

              {/* 위험 구역 (접을 수 있는 아코디언) */}
              <div className={styles.dangerZone}>
                <button
                  type="button"
                  className={styles.dangerFoldToggle}
                  aria-expanded={dangerFold}
                  onClick={() => setDangerFold((open) => !open)}
                >
                  <div className={styles.dangerFoldTitle}>
                    <ChevronDown12Regular
                      className={mergeClasses(styles.foldChevron, !dangerFold && styles.foldChevronClosed)}
                    />
                    <span>위험 구역</span>
                  </div>
                  <Caption1 className={styles.quiet}>
                    {dangerFold ? "접기" : "아카이브, 소유권 이전, 플랜 삭제"}
                  </Caption1>
                </button>

                {dangerFold ? (
                  <div className={styles.dangerContent}>
                    <div className={styles.dangerRow}>
                      <div className={styles.dangerRowText}>
                        <div className={styles.dangerRowTitle}>플랜 아카이브</div>
                        <Caption1 className={styles.quiet}>
                          {archived
                            ? "현재 아카이브된 읽기 전용 상태입니다. 복구하면 다시 일정을 편집할 수 있습니다."
                            : "플랜을 읽기 전용으로 아카이브합니다. 언제든 다시 활성화할 수 있습니다."}
                        </Caption1>
                      </div>
                      {archived ? (
                        <FuiButton appearance="outline" density="dialog" onClick={() => useProjectStore.getState().setLifecycle("active")}>
                          복구
                        </FuiButton>
                      ) : (
                        <FuiButton appearance="outline" density="dialog" onClick={() => { setConfirmName(""); setArchiveOpen(true); }}>
                          아카이브
                        </FuiButton>
                      )}
                    </div>

                    {actor.role === "owner" && (
                      <div className={styles.dangerRow}>
                        <div className={styles.dangerRowText}>
                          <div className={styles.dangerRowTitle}>소유권 이전</div>
                          <Caption1 className={styles.quiet}>
                            {transferTarget
                              ? `이 플랜의 소유권을 관리자 「${transferTarget.name}」에게 넘깁니다.`
                              : "활성 관리자가 없으면 소유권을 넘길 수 없습니다."}
                          </Caption1>
                        </div>
                        <FuiButton appearance="outline" density="dialog" disabled={!transferTarget} onClick={() => setReauth("ask")}>
                          소유권 이전
                        </FuiButton>
                      </div>
                    )}

                    {actor.role === "owner" && !archived ? (
                      <div className={styles.dangerRow}>
                        <div className={styles.dangerRowText}>
                          <div className={styles.dangerRowTitle}>플랜 삭제</div>
                          <Caption1 className={styles.quiet}>
                            플랜을 휴지통으로 이동합니다. 30일 후 모든 태스크와 일정이 영구 삭제됩니다.
                          </Caption1>
                        </div>
                        <FuiButton appearance="outline" density="dialog" onClick={() => setDeleteOpen(true)}>
                          삭제
                        </FuiButton>
                      </div>
                    ) : null}

                    <div className={styles.dangerRow}>
                      <div className={styles.dangerRowText}>
                        <div className={styles.dangerRowTitle}>플랜 나가기</div>
                        <Caption1 className={styles.quiet}>
                          {actor.role === "owner"
                            ? "소유자는 소유권을 이전한 뒤에만 나갈 수 있습니다."
                            : access.departed
                            ? `이 플랜에서 나갔습니다. ${leaveImpactSentence()}`
                            : "플랜 목록에서 빠지며 배정된 작업에 접근할 수 없게 됩니다."}
                        </Caption1>
                      </div>
                      {actor.role !== "owner" && !access.departed ? (
                        <FuiButton appearance="outline" density="dialog" onClick={() => setLeaveOpen(true)}>
                          나가기
                        </FuiButton>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          )}
          {tab === "members" && (
            <div className={styles.fill}>
            <MemberRoster
              members={access.members}
              invites={access.invites}
              requests={access.requests}
              canReview={canReview}
              listTab={memberTab}
              onListTab={setMemberTab}
              actorRole={planRoleLabel(actor.role)}
              toolbarAction={canReview ? (
                <div className={styles.row}>
                  <FuiButton appearance="outline" density="dialog" onClick={() => setGrant("team")}>
                    팀 추가
                  </FuiButton>
                  <FuiButton appearance="primary" density="dialog" onClick={() => setGrant("member")}>
                    멤버 추가
                  </FuiButton>
                </div>
              ) : undefined}
              teamOf={(person) => directory.find((row) => row.email.toLowerCase() === person.email.toLowerCase())?.teamName ?? ""}
              onRemove={removeMockMember}
              onSuspend={suspendMockMember}
              onReactivate={reactivateMockMember}
              onCancelInvite={cancelMockInvite}
              onResend={resendMockInvite}
              onRole={setMockMemberRole}
              onCancelRequest={cancelMockAccessRequest}
            />
            </div>
          )}
          {tab === "security" && (
            <div className={styles.general}>
              {/* 1. 공용 초대 링크 카드 */}
              <div className={styles.card}>
                <div className={styles.cardHead}>
                  <div className={styles.cardTitle}>공용 초대 링크</div>
                  <Caption1 className={styles.cardSubtitle}>
                    초대 링크를 소지한 누구나 뷰어로 플랜에 참가할 수 있습니다.
                  </Caption1>
                </div>

                <div style={{ paddingTop: "4px" }}>
                  <DialogCheckbox
                    label="공용 초대 링크를 활성화합니다"
                    checked={access.publicInvite.enabled}
                    onChange={(next) => {
                      setPublicInviteEnabled(next);
                      setLinkCopied(false);
                    }}
                  />
                </div>

                {access.publicInvite.enabled ? (
                  <>
                    <div className={styles.linkCardBox}>
                      <span className={mergeClasses(styles.quiet, styles.path)} title={publicInviteCopyText(access.publicInvite.id)}>
                        {publicInviteCopyText(access.publicInvite.id)}
                      </span>
                      <div className={styles.linkActions}>
                        {linkCopied ? (
                          <Badge size="small" appearance="tint" color="success">
                            복사됨
                          </Badge>
                        ) : null}
                        <FuiButton
                          appearance="secondary"
                          density="dialog"
                          icon={<Copy16Regular />}
                          onClick={() => {
                            void navigator.clipboard?.writeText(publicInviteCopyText(access.publicInvite.id)).catch(() => undefined);
                            setLinkCopied(true);
                          }}
                        >
                          링크 복사
                        </FuiButton>
                        <FuiButton
                          appearance="subtle"
                          density="dialog"
                          icon={<ArrowRotateClockwise16Regular />}
                          onClick={() => setRotateOpen(true)}
                        >
                          회전(재발급)
                        </FuiButton>
                      </div>
                    </div>
                    <Caption1 className={styles.quiet}>{PUBLIC_INVITE_NOTE}</Caption1>
                  </>
                ) : (
                  <Caption1 className={styles.quiet}>{PUBLIC_INVITE_OFF}</Caption1>
                )}
              </div>

              {/* 2. 읽기 전용 링크 공유 카드 */}
              <div className={styles.card}>
                <div className={styles.cardHead}>
                  <div className={styles.cardTitle}>외부 읽기 링크 공유 정책</div>
                  <Caption1 className={styles.cardSubtitle}>
                    로그인 없이 외부 고객이나 이해관계자가 실시간 현황을 확인할 수 있는 공유 링크를 제어합니다.
                  </Caption1>
                </div>

                <div style={{ paddingTop: "4px" }}>
                  <DialogCheckbox
                    label="읽기 링크 공유 기능을 사용합니다"
                    checked={access.shareEnabled}
                    onChange={setMockShareEnabled}
                  />
                </div>
                <Caption1 className={styles.quiet}>{sharePolicySentence()}</Caption1>
              </div>
            </div>
          )}
          {tab === "share" && (
            access.shareEnabled ? (
              <div className={styles.fill}>
                <ShareLinkList
                  shares={access.shares}
                  detailId={shareDetailId}
                  onDetailId={setShareDetailId}
                  action={<NewShareButton />}
                />
              </div>
            ) : (
              <GhostEmptyState compact title="읽기 링크가 꺼져 있습니다" description="보안에서 다시 켤 수 있습니다." />
            )
          )}
          {tab === "audit" && (
            <div className={styles.fill}>
              <div className={styles.cardHead} style={{ padding: "4px 0 8px" }}>
                <div className={styles.cardTitle}>플랜 감사 로그</div>
                <Caption1 className={styles.cardSubtitle}>
                  플랜 내 멤버 초대, 권한 변경, 공유 링크 발행 및 일정 기준선 등 주요 활동 이력입니다.
                </Caption1>
              </div>
              <ListToolbar
                edge="table"
                filters={
                  <>
                    <FuiFilterSelect
                      density="dialog"
                      label="기간"
                      ariaLabel="기간 필터"
                      value={auditPeriod}
                      options={AUDIT_PERIODS}
                      onValueChange={setAuditPeriod}
                      mountNode={layer}
                    />
                    <FuiFilterSelect
                      density="dialog"
                      label="행위"
                      ariaLabel="행위 필터"
                      value={auditAction}
                      options={auditActions}
                      onValueChange={setAuditAction}
                      mountNode={layer}
                    />
                  </>
                }
              />
              <div className={mergeClasses(table.frame, styles.frame)}>
                <table className={mergeClasses(table.table, styles.fixed)} aria-label="감사">
                  <colgroup>
                    <col style={{ width: 110 }} />
                    <col style={{ width: 80 }} />
                    <col style={{ width: 130 }} />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className={table.th}>시각</th>
                      <th className={table.th}>수행자</th>
                      <th className={table.th}>행위</th>
                      <th className={table.th}>상세 내용</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditShown.map((row) => (
                      <tr
                        key={row.id}
                        className={styles.auditRow}
                        tabIndex={0}
                        aria-label={`${row.what} 자세히`}
                        onClick={() => setAuditId(row.id)}
                        onKeyDown={(event) => {
                          if (event.key !== "Enter" && event.key !== " ") return;
                          event.preventDefault();
                          setAuditId(row.id);
                        }}
                      >
                        <td className={table.td}>{formatDate(row.when, "numeric")}</td>
                        <td className={mergeClasses(table.td, styles.clip)}>
                          <div className={styles.name} title={row.who}>{row.who}</div>
                        </td>
                        <td className={mergeClasses(table.td, styles.clip)}>
                          <Badge size="small" appearance="tint" color="informative">
                            {row.what}
                          </Badge>
                        </td>
                        <td className={table.td}>
                          <div className={styles.name} title={row.detail}>{row.detail}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          </div>
  );

  const overlays = (
    <>
      <AuditDetailDrawer row={auditOpen} onClose={() => setAuditId(null)} />
      <AppDialog
        open={leave != null}
        onClose={() => setLeave(null)}
        width="sm"
        title="변경을 버릴까요?"
        footer={
          <DialogActionsBar
            cancelLabel="머무르기"
            confirmLabel="버리기"
            onCancel={() => {
              pendingSection.current = null;
              setLeave(null);
              if (embedded) onSection?.(tab);
            }}
            onConfirm={confirmLeave}
          />
        }
      >
        <Body1>
          {leave === "close"
            ? "저장하지 않은 이름과 설정이 사라집니다."
            : "저장하지 않은 이름과 설정을 버리고 다른 탭으로 이동합니다."}
        </Body1>
      </AppDialog>
      {grant === "member" ? (
        <PlanMemberAddDialog
          open
          people={grantPeople.filter((person) => addCandidates.some((candidate) => candidate.id === person.id))}
          onClose={() => setGrant(null)}
          onAdd={addSelected}
          onInviteGuest={inviteGuest}
        />
      ) : null}
      {grant === "team" ? (
        <PlanTeamAddDialog
          open
          teams={teamChoices}
          onClose={() => setGrant(null)}
          onAdd={(teamId) => addMockPlanMembers(teamGrantRows(grantPeople, teamId, rosterEmails))}
        />
      ) : null}
      <AppDialog
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        width="sm"
        title="플랜 아카이브"
        description={`이 플랜은 읽기 전용이 됩니다. 멤버는 계속 볼 수 있지만 편집할 수 없습니다. ${archiveShareSentence()}`}
        titleAside={
          <MockCaseMenu
            cases={[
              { id: "mismatch", label: "이름이 달라 아카이브할 수 없음" },
              { id: "match", label: "이름이 같아 아카이브할 수 있음" },
            ]}
            onPick={(id) => setConfirmName(id === "match" ? planName : "")}
          />
        }
        footer={
          <>
            <FuiButton appearance="outline" density="dialog" onClick={() => setArchiveOpen(false)}>
              취소
            </FuiButton>
            <FuiButton
              appearance="primary"
              density="dialog"
              disabled={confirmName !== planName}
              onClick={() => {
                useProjectStore.getState().setLifecycle("archived");
                pauseSharesOnArchive();
                setArchiveOpen(false);
                onClose();
              }}
            >
              아카이브
            </FuiButton>
          </>
        }
      >
        <DialogField label="확인하려면 플랜 이름을 입력하세요" hint={planName}>
          <Input size="small" value={confirmName} onChange={(_, data) => setConfirmName(data.value)} />
        </DialogField>
      </AppDialog>
      <AppDialog
        open={reauth != null}
        onClose={() => setReauth(null)}
        width="sm"
        title="다시 로그인"
        description={reauthPromptSentence()}
        titleAside={
          <MockCaseMenu
            cases={[
              { id: "fresh", label: "최근 로그인" },
              { id: "stale", label: "로그인이 오래됨" },
            ]}
            onPick={(id) => {
              if (id === "fresh") {
                setReauth(null);
                setTransferOpen(true);
              } else {
                setReauth("blocked");
              }
            }}
          />
        }
        footer={
          <FuiButton appearance="outline" density="dialog" onClick={() => setReauth(null)}>
            닫기
          </FuiButton>
        }
      >
        {reauth === "blocked" ? <Caption1>{reauthBlockSentence()}</Caption1> : null}
      </AppDialog>
      <ConfirmDialog
        stacked
        open={rotateOpen}
        title="공용 링크 회전"
        message="이전 링크는 바로 쓸 수 없게 됩니다."
        confirmLabel="회전"
        cancelLabel="취소"
        onCancel={() => setRotateOpen(false)}
        onConfirm={() => {
          rotatePublicInvite();
          setLinkCopied(false);
          setRotateOpen(false);
        }}
      />
      <ConfirmDialog
        stacked
        open={transferOpen && transferTarget != null}
        title="소유권 이전"
        message={<>소유권을 이전할까요? {transferOwnershipImpact()}</>}
        confirmLabel="이전"
        cancelLabel="취소"
        onCancel={() => setTransferOpen(false)}
        onConfirm={() => {
          if (transferTarget) transferMockOwnership(transferTarget.id);
          setTransferOpen(false);
        }}
      />
      <ConfirmDialog
        stacked
        open={deleteOpen}
        title="플랜 삭제"
        message="플랜을 삭제할까요? 일정과 태스크 등 모든 데이터가 함께 삭제됩니다."
        confirmLabel="삭제"
        cancelLabel="취소"
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => {
          const id = useProjectStore.getState().activeId;
          setDeleteOpen(false);
          if (!id) return;
          useProjectStore.getState().closeProject();
          useCatalogStore.getState().deleteProject(id);
          onClose();
        }}
      />
      <ConfirmDialog
        stacked
        open={leaveOpen}
        title="플랜 나가기"
        message={<>이 플랜을 나갈까요? {leaveImpactSentence()}</>}
        confirmLabel="나가기"
        cancelLabel="취소"
        onCancel={() => setLeaveOpen(false)}
        onConfirm={() => {
          leaveMockPlan(planRoleLabel(actor.role));
          setLeaveOpen(false);
        }}
      />
    </>
  );

  if (embedded) {
    return (
      <>
        {settingsPane}
        {overlays}
      </>
    );
  }

  return (
    <>
      <AppDialog
        open={open}
        onClose={requestClose}
        width="xl"
        height={720}
        className="max-h-[calc(100dvh-48px)]"
        persistWhile={auditOpen != null || shareDetailId != null || grant != null || leave != null}
        modal={auditOpen == null && shareDetailId == null && grant == null && leave == null}
        title={UI_TERMS.planSettings.ko}
        titleAside={<MockCaseMenu cases={SETTINGS_CASES} onPick={pickCase} />}
      >
        <div className={styles.shell}>
          <TabList
            className={styles.tabs}
            vertical
            size="small"
            selectedValue={tab}
            aria-label="플랜 설정"
            onTabSelect={(_, data) => {
              if (typeof data.value === "string") selectTab(data.value as PlanSettingsTab);
            }}
          >
            {TABS.map((item) => (
              <Tab key={item.value} className={styles.tab} value={item.value}>
                {item.label}
              </Tab>
            ))}
          </TabList>
          {settingsPane}
        </div>
      </AppDialog>
      {overlays}
    </>
  );
}

/** 감사 한 줄. 이 브라우저의 목업이라 지우는 버튼은 없다. */
function AuditDetailDrawer({ row, onClose }: { row: MockAuditRow | null; onClose: () => void }) {
  const styles = useStyles();
  const dialogLayer = useDialogLayer();
  const layer = dialogLayer ?? (typeof document !== "undefined" ? document.body : undefined);

  useEffect(() => {
    if (!row) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [row, onClose]);

  if (!row || !layer) return null;
  return (
    <OverlayDrawer
      open
      mountNode={layer}
      position="end"
      modalType="modal"
      className={styles.drawer}
      style={{ position: "fixed", top: 0, right: 0, height: "100vh", zIndex: 60 }}
      onOpenChange={(_, data) => {
        if (!data.open) onClose();
      }}
      aria-label="감사 기록"
    >
      <div data-dialog-local-escape="">
        <DrawerHeader>
          <div className={styles.drawerHead}>
            <Caption1 className={styles.quiet}>{formatDate(row.when, "numeric")}</Caption1>
            <FuiButton appearance="subtle" icon={<Dismiss24Regular />} aria-label="닫기" onClick={onClose} />
          </div>
          <div className={styles.name} title={row.what}>{row.what}</div>
        </DrawerHeader>
        <DrawerBody className={styles.drawerBody}>
          <Caption1 className={styles.quiet}>{row.who}</Caption1>
          <div>{row.detail}</div>
        </DrawerBody>
      </div>
    </OverlayDrawer>
  );
}
